/**
 * CreateJobModal Component
 * Modal for creating new analysis jobs with carousel-based job type selection
 *
 * STEP-BASED FLOW:
 * Step 1: Carousel for selecting job type
 * Step 2: Selected job info + File upload (carousel hidden)
 */

import React, { useState, useEffect } from "react";
import { Modal, FileUpload, Button, ErrorAlert, Badge } from "../common";
import { CameraConfigForm, validateCameraConfig } from "./CameraConfigForm";
import { JobType, CameraConfig } from "../../types";
import { useAppDispatch, jobsActions, uiActions } from "../../redux";
import {
  JOB_TYPE_CONFIGS,
  getActiveJobTypes,
  isConfigBasedJobType,
  JobTypeConfig,
} from "../../services/jobTypes";
import { getJobIcon } from "../../utils/icons";
import { validateJobFile } from "../../utils/validation.utils";
import {
  ChevronLeft,
  ChevronRight,
  BarChart3,
  ArrowLeft,
} from "lucide-react";

const CARDS_PER_PAGE = 4;

interface CreateJobModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  selectedJobType: JobType | null;
}

export const CreateJobModal: React.FC<CreateJobModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  selectedJobType,
}) => {
  const dispatch = useAppDispatch();
  const [step, setStep] = useState<1 | 2>(1);
  const [currentPage, setCurrentPage] = useState(0);
  const [jobType, setJobType] = useState<JobType | "">("");
  const [file, setFile] = useState<File | null>(null);
  const [cameraConfig, setCameraConfig] = useState<CameraConfig[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Get active job types
  const activeJobTypes = getActiveJobTypes();
  const totalPages = Math.ceil(activeJobTypes.length / CARDS_PER_PAGE);

  // Visible jobs for current page
  const visibleJobs = activeJobTypes.slice(
    currentPage * CARDS_PER_PAGE,
    (currentPage + 1) * CARDS_PER_PAGE
  );

  // Navigation
  const handlePrev = () => setCurrentPage((p) => Math.max(0, p - 1));
  const handleNext = () =>
    setCurrentPage((p) => Math.min(totalPages - 1, p + 1));

  // Pre-selected job type
  useEffect(() => {
    if (selectedJobType) {
      setJobType(selectedJobType);
      setStep(2);
    }
  }, [selectedJobType]);

  // Reset on job type change
  useEffect(() => {
    setFile(null);
    setCameraConfig([]);
    setError(null);
  }, [jobType]);

  // Reset on modal open
  useEffect(() => {
    if (isOpen && !selectedJobType) {
      setStep(1);
      setCurrentPage(0);
      setJobType("");
    }
  }, [isOpen, selectedJobType]);

  const selectedConfig = jobType ? JOB_TYPE_CONFIGS[jobType as JobType] : null;
  const isConfigBased = jobType
    ? isConfigBasedJobType(jobType as JobType)
    : false;

  // Select job type and go to step 2
  const handleSelectJobType = (config: JobTypeConfig) => {
    setJobType(config.type);
    setStep(2);
  };

  // Go back to step 1
  const handleBack = () => {
    setStep(1);
    setFile(null);
    setCameraConfig([]);
    setError(null);
  };

  // Submit
  const handleSubmit = async () => {
    if (!jobType) {
      setError("Please select a job type");
      return;
    }

    const config = JOB_TYPE_CONFIGS[jobType as JobType];

    if (isConfigBased) {
      const configValidation = validateCameraConfig(cameraConfig);
      if (!configValidation.isValid) {
        setError(configValidation.error || "Invalid configuration");
        return;
      }
    } else {
      const fileValidation = validateJobFile(
        file,
        config.acceptedFileTypes,
        config.maxFileSize
      );
      if (!fileValidation.isValid) {
        setError(fileValidation.error || "Invalid file");
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);

      if (isConfigBased) {
        await dispatch(
          jobsActions.createConfigJob({
            jobType: jobType as JobType,
            config: { cameras: cameraConfig },
          })
        ).unwrap();
      } else {
        await dispatch(
          jobsActions.createJob({
            jobType: jobType as JobType,
            file: file!,
            onProgress: (progress) => {
              setUploadProgress(progress);
              dispatch(uiActions.setUploadProgress(progress));
            },
          })
        ).unwrap();
      }

      dispatch(
        uiActions.addNotification({
          type: "success",
          message: isConfigBased
            ? "Camera system configured successfully!"
            : "Job created successfully! Processing will begin shortly.",
        })
      );

      // Reset
      setJobType("");
      setFile(null);
      setCameraConfig([]);
      setUploadProgress(0);
      setCurrentPage(0);
      setStep(1);

      onClose();
      if (onSuccess) onSuccess();
    } catch (err: any) {
      setError(err?.message || err || "Failed to create job");
    } finally {
      setLoading(false);
      dispatch(uiActions.resetUploadProgress());
    }
  };

  // Close modal
  const handleClose = () => {
    if (!loading) {
      setJobType("");
      setFile(null);
      setCameraConfig([]);
      setError(null);
      setUploadProgress(0);
      setCurrentPage(0);
      setStep(1);
      onClose();
    }
  };

  // Helper text
  const getUploadLabel = () => {
    if (!selectedConfig) return "Upload File";
    if (
      selectedConfig.acceptedExtensions?.some((ext) =>
        [".stl", ".obj", ".ply", ".step", ".stp"].includes(ext.toLowerCase())
      )
    ) {
      return "Upload 3D Model";
    }
    return "Upload Video or Image";
  };

  const getHelperText = () => {
    if (!selectedConfig) return "";
    const extensions =
      selectedConfig.acceptedExtensions.length > 0
        ? selectedConfig.acceptedExtensions.join(", ")
        : selectedConfig.acceptedFileTypes.join(", ");
    return `Accepted formats: ${extensions} • Max size: ${selectedConfig.maxFileSize}MB`;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Create New Analysis"
      size="xl"
    >
      <div className="space-y-4">
        {/* Error */}
        {error && (
          <ErrorAlert message={error} onDismiss={() => setError(null)} />
        )}

        {/* STEP 1: Job Type Selection Carousel */}
        {step === 1 && (
          <>
            {/* Header */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Select Analysis Type</h3>
                <p className="text-sm text-slate-400">
                  Choose the type of analysis to run on your media
                </p>
              </div>
            </div>

            {/* Carousel */}
            <div className="flex items-center gap-2">
              {/* Left Arrow */}
              <button
                onClick={handlePrev}
                disabled={currentPage === 0}
                className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                  currentPage === 0
                    ? "text-slate-600 cursor-not-allowed"
                    : "text-slate-400 hover:text-white hover:bg-slate-700/50"
                }`}
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              {/* Cards */}
              <div className="flex-1 grid grid-cols-4 gap-3">
                {visibleJobs.map((config) => (
                  <button
                    key={config.type}
                    onClick={() => handleSelectJobType(config)}
                    className={`group text-left p-4 rounded-xl border transition-all hover:scale-[1.02] ${
                      jobType === config.type
                        ? "bg-cyan-500/10 border-cyan-500"
                        : "bg-slate-800/60 border-slate-700/50 hover:border-cyan-500/50"
                    }`}
                  >
                    {/* Icon */}
                    <div
                      className={`w-12 h-12 rounded-lg flex items-center justify-center mb-3 ${
                        jobType === config.type
                          ? "bg-cyan-500/20 text-cyan-400"
                          : "bg-slate-700/60 text-cyan-400"
                      }`}
                    >
                      {getJobIcon(config.iconName, "w-6 h-6")}
                    </div>

                    {/* Title */}
                    <h4
                      className={`font-semibold text-sm mb-1 ${
                        jobType === config.type
                          ? "text-cyan-400"
                          : "text-white"
                      }`}
                    >
                      {config.label}
                    </h4>

                    {/* Description */}
                    <p className="text-xs text-slate-400 line-clamp-2 mb-3 min-h-[32px]">
                      {config.description}
                    </p>

                    {/* Badge */}
                    <Badge
                      variant={
                        config.category === "advanced" ? "info" : "primary"
                      }
                      size="sm"
                    >
                      {config.category === "advanced" ? "Advanced" : "Standard"}
                    </Badge>
                  </button>
                ))}
              </div>

              {/* Right Arrow */}
              <button
                onClick={handleNext}
                disabled={currentPage === totalPages - 1}
                className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                  currentPage === totalPages - 1
                    ? "text-slate-600 cursor-not-allowed"
                    : "text-slate-400 hover:text-white hover:bg-slate-700/50"
                }`}
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* Page Dots */}
            <div className="flex items-center justify-center gap-1.5">
              {Array.from({ length: totalPages }).map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentPage(idx)}
                  className={`h-2 rounded-full transition-all ${
                    idx === currentPage
                      ? "bg-cyan-500 w-5"
                      : "bg-slate-600 w-2 hover:bg-slate-500"
                  }`}
                />
              ))}
            </div>

            {/* Counter */}
            <p className="text-center text-slate-500 text-xs">
              Showing {currentPage * CARDS_PER_PAGE + 1}-
              {Math.min(
                (currentPage + 1) * CARDS_PER_PAGE,
                activeJobTypes.length
              )}{" "}
              of {activeJobTypes.length} analysis types
            </p>
          </>
        )}

        {/* STEP 2: Selected Job + File Upload */}
        {step === 2 && selectedConfig && (
          <>
            {/* Back Button */}
            <button
              onClick={handleBack}
              className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Change analysis type</span>
            </button>

            {/* Selected Job Info */}
            <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-5">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-cyan-500/20 flex items-center justify-center text-cyan-400">
                  {getJobIcon(selectedConfig.iconName, "w-6 h-6")}
                </div>
                <div>
                  <h4 className="text-white font-semibold">
                    {selectedConfig.label}
                  </h4>
                  <p className="text-slate-400 text-sm">
                    {selectedConfig.description}
                  </p>
                </div>
              </div>
            </div>

            {/* File Upload OR Config Form */}
            {isConfigBased ? (
              <CameraConfigForm
                onConfigChange={setCameraConfig}
                maxCameras={10}
              />
            ) : (
              <FileUpload
                label={getUploadLabel()}
                accept={selectedConfig.acceptedFileTypes}
                maxSize={selectedConfig.maxFileSize}
                onFileSelect={setFile}
                onError={(err) => setError(err)}
                helperText={getHelperText()}
              />
            )}

            {/* Upload Progress */}
            {loading && !isConfigBased && uploadProgress > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-400">Uploading...</span>
                  <span className="text-cyan-400 font-medium">
                    {uploadProgress}%
                  </span>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-2">
                  <div
                    className="bg-cyan-500 h-2 rounded-full transition-all"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Config Loading */}
            {loading && isConfigBased && (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500" />
                <span className="ml-3 text-slate-400">
                  Configuring cameras...
                </span>
              </div>
            )}
          </>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <Button
            variant="ghost"
            onClick={handleClose}
            disabled={loading}
            fullWidth
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={
              !jobType ||
              loading ||
              (isConfigBased ? cameraConfig.length === 0 : !file)
            }
            loading={loading}
            fullWidth
          >
            Create Job
          </Button>
        </div>
      </div>
    </Modal>
  );
};
